import { auto, IHttpResponse, IHttpService, IQService, ITemplateCacheService, IPromise, equals, copy, IScope } from "angular";
import { IModalServiceInstance, IModalService, IModalSettings, IModalInstanceService } from "angular-ui-bootstrap";
import { Ng1StateDeclaration, StateService, StateParams, TransitionService } from "@uirouter/angularjs";
import { getLanguageCode } from "@gtmhub/localization";
import { storage } from "@gtmhub/core/storage";

export const camelToKebabCase = (component: string): string => component.replace(/([A-Z])/g, "-$1").toLowerCase();

export const fixModalsAbstractStatesIE = (): void => {
  if (!window.document.activeElement || window.document.activeElement.tagName !== "BODY") {
    window.document.body.focus();
  }
};

export interface IModalComponent<Resolve = Record<string, unknown>> {
  close(args?: { $value: unknown }): void;
  dismiss(args?: { $value: unknown }): void;
  modalInstance: IModalInstanceService;
  resolve: Resolve;
}

export type IModalState = Omit<
  Ng1StateDeclaration,
  "template" | "templateUrl" | "templateProvider" | "controller" | "controllerProvider" | "controllerAs" | "onEnter" | "onExit"
> & {
  component?: string;
  componentBindingToStateParamMap?: Record<string, string>;
  view?: string;
  windowClass?: string;
  backdrop?: boolean;
  backdropClass?: string;
};

interface IStateSnapshot {
  name: string;
  params: StateParams;
}

const openerStack: IStateSnapshot[] = [];

interface IModalUtilScope extends IScope {
  mousedown(): void;
  mouseup(): void;
}

const createStateInitiatedModalSettings = (state: IModalState): IModalSettings => {
  const modalConfig: IModalSettings = {
    backdrop: state.backdrop !== false,
    windowClass: state.windowClass ? state.windowClass + " state-initiated" : "state-initiated",
    backdropClass: state.backdropClass || "",
    controller: [
      "$scope",
      function ($scope: IModalUtilScope) {
        storage.remove("modalCloseReason");
        $scope.mousedown = function () {
          storage.set("preventAccidentalOutisdeClick", true);
        };
        $scope.mouseup = function () {
          storage.set("preventAccidentalOutisdeClick", false);
        };

        $scope.$on("modal.closing", function (event) {
          if (storage.get("preventAccidentalOutisdeClick") === true) {
            storage.set("preventAccidentalOutisdeClick", false);
            event.preventDefault();
          }
        });
      },
    ],
  };

  // the new router does not accept Component + Template at the same time. The Component bindings have been handled in the routing
  if (!state.component) {
    modalConfig.template = `<div ui-view='${state.view}' ng-mousedown="mousedown()" ng-mouseup="mouseup()"></div>`;
  } else {
    modalConfig.component = state.component;
  }

  return modalConfig;
};

const handleStateInitiatedModalCloseByStateChange = ($state: StateService) => {
  // reset the opener stack if we are going to a previous state in the stack
  const newStateName = $state.current.name;
  const newStateParams = copy($state.params); // TOOD: copy($injector.get("$stateParams"));

  const stateIndex = openerStack.findIndex((x) => x.name === newStateName && equals(x.params, newStateParams));
  if (stateIndex >= 0) {
    openerStack.splice(stateIndex);
  }
};

const handleStateInitiatedModalCloseByModalClose = (
  $state: StateService,
  options: { returnState: string; opener: IStateSnapshot; abstract: boolean; beforeNavigationState: IStateSnapshot }
) => {
  let gotoPromise: IPromise<unknown>;
  if (options.returnState) {
    gotoPromise = $state.go(options.returnState);
  } else {
    if (options.opener) {
      // check if reload: true brings issues to all the e2e tests
      gotoPromise = $state.go(options.opener.name, options.opener.params);
    } else {
      // we have opened the URL directly, so we don't know the opener
      gotoPromise = $state.go(options.abstract ? "^.^" : "^");
    }
  }

  gotoPromise.then(() => {
    // prevent endless loop when going back
    if (openerStack.length) {
      popLast(options.beforeNavigationState);
    }
  });
};

/**
 * Opens a modal when entering a state and closes it when exiting. When using
 * $close() / $dismiss() it will automatically return to the opener, unless
 * returnState / cancelState was provided in the state params.
 */
export const modal = (state: IModalState): Ng1StateDeclaration => {
  if (!state.component && !state.view) {
    console.warn("If you haven't provided neither component option nor view option, a div with no ui-view name will be rendered");
  }

  let modalInstance: IModalServiceInstance;

  return {
    name: state.name,
    abstract: state.abstract,
    url: state.url,
    data: {
      ...(state.data || {}),
      modal: true,
    },
    resolve: state.resolve,
    params: {
      ...(state.params || {}),
      returnState: null,
      cancelState: null,
    },
    template: "",
    onEnter: [
      "$uibModal",
      "$state",
      "$injector",
      "$transitions",
      ($uibModal: IModalService, $state: StateService, $injector: auto.IInjectorService, $transitions: TransitionService) => {
        fixModalsAbstractStatesIE();

        const stateSnapshot = (): IStateSnapshot => ({
          name: $state.current.name,
          params: copy($injector.get("$stateParams")),
        });

        // This is the state before we navigate to the new modal state
        const prevState = stateSnapshot();

        // if we are opening the modal from a certain state only
        // (the first request comes from the default state "")
        const ignoredStates = ["accountBootstrap.resolveAccount"];
        if (prevState.name && !ignoredStates.includes(prevState.name)) {
          openerStack.push(prevState);
        }

        const modalSettings = createStateInitiatedModalSettings(state);

        const oneTimeUnregisterHook = $transitions.onSuccess({}, () => {
          modalInstance = $uibModal.open(modalSettings);

          modalInstance.result
            .then(
              () => {
                const $stateParams = $injector.get<StateParams>("$stateParams");
                return { reason: null, returnState: ($stateParams.returnState || $stateParams.cancelState) as string };
              },
              (reason: unknown) => {
                const $stateParams = $injector.get<StateParams>("$stateParams");
                return { reason, returnState: ($stateParams.cancelState || $stateParams.returnState) as string };
              }
            )
            .then(({ reason, returnState }) => {
              storage.set("modalCloseReason", reason);
              // we have called dismiss() / close() on the modal instance from a controller or backdrop
              if (reason === "exiting") {
                $state.transition.promise.then(() => handleStateInitiatedModalCloseByStateChange($state));
                return;
              }

              const opener = openerStack.pop();
              const beforeNavigationState = stateSnapshot();

              handleStateInitiatedModalCloseByModalClose($state, { returnState, opener, abstract: state.abstract, beforeNavigationState });
            });

          oneTimeUnregisterHook();
        });
      },
    ],
    onExit: () => {
      // if we have a modal instance it means we are exiting this state using `$state.go`
      // or clicking on an element with ui-sref directive
      if (modalInstance) {
        modalInstance.dismiss("exiting");
        modalInstance = null;
      }
    },
  };
};

const popLast = (beforeNavigationState: IStateSnapshot): void => {
  const lastOpener = openerStack[openerStack.length - 1];
  if (equals(lastOpener, beforeNavigationState)) {
    openerStack.pop();
    popLast(beforeNavigationState);
  }
};

export const async = <StateMap extends Record<string, Ng1StateDeclaration> | Ng1StateDeclaration[]>(
  moduleLoader: () => Promise<{ default: string }>,
  stateMap: StateMap
): StateMap => {
  const _loadModule = [
    "$q",
    "$ocLazyLoad",
    ($q: IQService, $ocLazyLoad: oc.ILazyLoad) =>
      $q((resolve, reject) => {
        moduleLoader()
          .then((result) => {
            const { default: moduleName } = result;
            return $ocLazyLoad.load({ name: moduleName });
          })
          .then(resolve, (rejection) => {
            console.error(rejection);
            reject(rejection);
          });
      }),
  ];

  const states = Array.isArray(stateMap) ? stateMap : Object.values(stateMap);
  for (const state of states) {
    if (!state.resolve) {
      state.resolve = {};
    }

    state.resolve._loadModule = _loadModule;
  }

  return stateMap;
};

export const urlFromMap = (urlMap: Record<string, string>): string => urlMap[getLanguageCode()];

export interface IFeatureToggleView {
  viewName?: string;
  featureDisabled: {
    ctrl: string;
    view: Record<string, string>;
  };
  featureEnabled: {
    ctrl: string;
    view: Record<string, string>;
  };
}

export const featureToggleView = (data: IFeatureToggleView & { isFeatureAvailable: string }): Partial<Ng1StateDeclaration> => {
  const view = {
    controllerProvider: [data.isFeatureAvailable, (featureEnabled: boolean) => (featureEnabled ? data.featureEnabled.ctrl : data.featureDisabled.ctrl)],
    controllerAs: "$ctrl",
    templateProvider: [
      data.isFeatureAvailable,
      "$http",
      "$templateCache",
      (featureEnabled: boolean, $http: IHttpService, $templateCache: ITemplateCacheService) => {
        const view = urlFromMap(featureEnabled ? data.featureEnabled.view : data.featureDisabled.view);

        return $http.get(view, { cache: $templateCache, headers: { Accept: "text/html" } }).then((response) => response.data);
      },
    ],
  };

  return data.viewName ? { views: { [data.viewName]: view } } : view;
};

export type ResponseError = {
  statusCode: number;
  error: string;
};

export type EntityExistsResponseError = {
  resourceId: string;
  data: object;
  error: string;
};

export type UserDeactivatedResponse = ResponseError & { error: "user deactivated" };
export type UserDeletedResponse = ResponseError & { error: "could not resolve the user with the provided claims" };

export const isErrorResponse = (response: IHttpResponse<unknown>): response is IHttpResponse<ResponseError> => {
  if (!response.status || !response.data) {
    return false;
  }

  const r = response as IHttpResponse<ResponseError>;
  return r.data.error !== undefined && r.data.statusCode !== undefined;
};

export const isUserDeactivatedOrDeletedResponse = (response: IHttpResponse<unknown>): response is IHttpResponse<UserDeactivatedResponse | UserDeletedResponse> => {
  if (!isErrorResponse(response) || response.status !== 403) {
    return false;
  }

  return response.data.error === "user deactivated" || response.data.error === "could not resolve the user with the provided claims";
};

export const isEntityExistsResponse = (response: IHttpResponse<unknown>): response is IHttpResponse<EntityExistsResponseError> => {
  if (!response.status || !response.data || response.status !== 409) {
    return false;
  }

  const r = response as IHttpResponse<EntityExistsResponseError>;
  return r.data.error !== undefined && r.data.resourceId !== undefined && r.data.data !== undefined;
};
